import * as React from "react";
import { Calendar } from "../calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../popover";
import { Button } from "../button";
import { Input } from "../input";
import { cn } from "src/@/lib/utils";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { Trans } from "@lingui/react/macro";

interface DateTimePickerProps {
    date?: Date;
    setDate: (date: Date | undefined) => void;
    className?: string;
    disabledDate?: (current: Date) => boolean;
    showTime?: boolean;
    defaultPickerValue?: Date;
    isMultipleDaysShift?: boolean;
}

export function DateTimePicker({
    date,
    setDate,
    className,
    disabledDate,
    showTime = true,
    defaultPickerValue,
}: DateTimePickerProps) {
    const handleDateSelect = (selectedDate: Date | undefined) => {
        if (!selectedDate) return;
        if (disabledDate?.(selectedDate)) return;

        const newDate = new Date(selectedDate);

        // If we have an existing date, keep its time
        if (date) {
            newDate.setHours(date.getHours());
            newDate.setMinutes(date.getMinutes());
            newDate.setSeconds(0);
            newDate.setMilliseconds(0);
        } else if (defaultPickerValue) {
            // Use default picker value's time if no existing date
            newDate.setHours(defaultPickerValue.getHours());
            newDate.setMinutes(defaultPickerValue.getMinutes());
            newDate.setSeconds(0);
            newDate.setMilliseconds(0);
        }

        setDate(newDate);
    };

    const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const timeValue = e.target.value;
        if (!timeValue) return;

        // Create new date based on existing date or current date
        const newDate = date ? new Date(date) : new Date();

        const [hours, minutes] = timeValue.split(":").map(Number);
        newDate.setHours(hours);
        newDate.setMinutes(minutes);
        newDate.setSeconds(0);
        newDate.setMilliseconds(0);

        if (disabledDate?.(newDate)) return;

        setDate(newDate);
    };

    return (
        <div className="flex gap-2">
            <Popover modal>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        className={cn(
                            "justify-start text-left font-normal",
                            !date && "text-muted-foreground",
                            className,
                        )}
                    >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date ? (
                            format(date, "MM/dd/yyyy")
                        ) : (
                            <span><Trans>Pick date</Trans></span>
                        )}
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                    <Calendar
                        mode="single"
                        selected={date}
                        onSelect={handleDateSelect}
                        disabled={disabledDate}
                        defaultMonth={defaultPickerValue}
                        initialFocus
                    />
                </PopoverContent>
            </Popover>

            {showTime && (
                <Input
                    type="time"
                    className="w-[115px]"
                    value={date ? format(date, "HH:mm") : ""}
                    onChange={handleTimeChange}
                />
            )}
        </div>
    );
}
